import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Search, Package, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editStock, setEditStock] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/products`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUpdateStock = async (productId) => {
    const newStock = parseInt(editStock);
    if (isNaN(newStock) || newStock < 0) {
      toast.error('Ingresa un stock válido');
      return;
    }
    try {
      await axios.patch(`${API_URL}/api/products/${productId}/stock?quantity=${newStock}`, {}, { withCredentials: true });
      toast.success('Stock actualizado');
      setEditingId(null);
      fetchProducts();
    } catch (error) {
      toast.error('Error al actualizar stock');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  const lowStockProducts = products.filter(p => p.stock <= 5);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Inventario</h1>
        <p className="text-stone-500 mt-1">Control de stock en tiempo real</p>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid="low-stock-alert">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">Stock bajo detectado</p>
            <p className="text-sm text-red-600">
              {lowStockProducts.length} productos con stock menor o igual a 5 unidades
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          type="text"
          placeholder="Buscar por nombre o SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="inventory-search"
          className="pl-12 h-12 rounded-xl bg-white border-stone-200"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold">Producto</TableHead>
              <TableHead className="font-semibold">SKU</TableHead>
              <TableHead className="font-semibold">Categoría</TableHead>
              <TableHead className="font-semibold">Unidad</TableHead>
              <TableHead className="font-semibold text-right">Costo</TableHead>
              <TableHead className="font-semibold text-right">Precio</TableHead>
              <TableHead className="font-semibold text-center">Stock</TableHead>
              <TableHead className="font-semibold text-right">Valor Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-stone-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  No hay productos en inventario
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow
                  key={product.id}
                  className={`hover:bg-stone-50/50 ${product.stock <= 5 ? 'bg-red-50/30' : ''}`}
                  data-testid={`inventory-row-${product.id}`}
                >
                  <TableCell className="font-medium text-stone-900">{product.name}</TableCell>
                  <TableCell className="text-stone-600">{product.sku}</TableCell>
                  <TableCell className="text-stone-600">{product.category_name}</TableCell>
                  <TableCell className="text-stone-600 capitalize">{product.unit_type}</TableCell>
                  <TableCell className="text-right text-stone-600">{formatCurrency(product.cost)}</TableCell>
                  <TableCell className="text-right font-medium text-stone-900">{formatCurrency(product.price)}</TableCell>
                  <TableCell className="text-center">
                    {editingId === product.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <Input
                          type="number"
                          value={editStock}
                          onChange={(e) => setEditStock(e.target.value)}
                          className="w-20 h-8 text-center rounded-lg"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStock(product.id)}
                          className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700"
                        >
                          Guardar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                          className="h-8 px-3 rounded-lg"
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingId(product.id); setEditStock(product.stock.toString()); }}
                        data-testid={`edit-stock-${product.id}`}
                        className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                          product.stock <= 5
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                        }`}
                      >
                        {product.stock}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-stone-900">
                    {formatCurrency(product.cost * product.stock)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-sm text-stone-500">Total Productos</p>
            <p className="text-2xl font-bold text-stone-900">{products.length}</p>
          </div>
          <div>
            <p className="text-sm text-stone-500">Unidades en Stock</p>
            <p className="text-2xl font-bold text-stone-900">
              {products.reduce((sum, p) => sum + p.stock, 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-stone-500">Valor Total Inventario</p>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(products.reduce((sum, p) => sum + (p.cost * p.stock), 0))}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
